import { useState, useEffect, useCallback } from "react";
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { useAuth } from "../contexts/AuthContext";
import api from "../services/api";
import { downloadProtectedFile } from "../utils/openBlob";

const S = {
  card: { background: "#ffffff", border: "1px solid #e5e7eb", borderRadius: 12, padding: 20 },
  input: { background: "#f9fafb", border: "1px solid #d1d5db", borderRadius: 8, color: "#111827", padding: "8px 12px", fontSize: 13, width: "100%" },
  btn: { padding: "8px 16px", borderRadius: 8, border: "none", cursor: "pointer", fontSize: 13, fontWeight: 600 },
  kpi: { background: "#ffffff", border: "1px solid #e5e7eb", borderRadius: 12, padding: "20px 24px", flex: 1 },
  label: { display: "block", color: "#4b5563", fontSize: 11, marginBottom: 6 },
};

const CATEGORIES = ["Terrains", "Constructions", "Installations techniques", "Matériel informatique", "Matériel de bureau", "Mobilier", "Véhicules", "Brevets/Logiciels", "Agencements", "Autres"];
const PIE_COLORS = ["#1a9e72", "#185fa5", "#8b5cf6", "#f59e0b", "#ef4444", "#06b6d4", "#22c55e", "#e879f9", "#fb923c", "#4b5563"];

function fmt(n) { return (n || 0).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }); }

function StatusBadge({ status }) {
  const map = { actif: { l: "Actif", c: "#1a9e72" }, cédé: { l: "Cédé", c: "#f59e0b" }, mis_au_rebut: { l: "Rebut", c: "#ef4444" } };
  const { l, c } = map[status] || { l: status, c: "#4b5563" };
  return <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 20, fontWeight: 700, background: c + "22", color: c }}>{l}</span>;
}

const EMPTY_FORM = { name: "", reference: "", category: "Matériel informatique", acquisition_date: new Date().toISOString().slice(0, 10), acquisition_cost: "", depreciation_method: "linéaire", useful_life_years: 5, location: "", supplier: "", notes: "" };

export default function Immobilisations({ currentCompanyId, currentSociete }) {
  const { getToken } = useAuth();
  const [tab, setTab] = useState("dashboard");
  const [assets, setAssets] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [assetDetail, setAssetDetail] = useState(null);
  const [filters, setFilters] = useState({ q: "", category: "", status: "" });
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [previewPlan, setPreviewPlan] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [showDisposeModal, setShowDisposeModal] = useState(false);
  const [disposeForm, setDisposeForm] = useState({ disposal_date: new Date().toISOString().slice(0, 10), proceeds: "", notes: "" });
  const [toast, setToast] = useState(null);

  const showToast = (msg, type = "success") => { setToast({ msg, type }); setTimeout(() => setToast(null), 3500); };

  const call = useCallback(async (method, path, body = null) => {
    const res = await fetch("/api/immobilisations" + path, {
      method,
      headers: { Authorization: "Bearer " + getToken(), "Content-Type": "application/json" },
      ...(body ? { body: JSON.stringify(body) } : {}),
    });
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  }, [getToken]);

  const loadDashboard = useCallback(async () => {
    if (!currentCompanyId) return;
    try {
      const d = await call("GET", `/dashboard?company_id=${currentCompanyId}`);
      setDashboard(d);
    } catch { setDashboard(null); }
  }, [currentCompanyId, call]);

  const loadAssets = useCallback(async () => {
    if (!currentCompanyId) return;
    setLoading(true);
    try {
      const qs = new URLSearchParams({ company_id: currentCompanyId });
      if (filters.q) qs.set("q", filters.q);
      if (filters.category) qs.set("category", filters.category);
      if (filters.status) qs.set("status", filters.status);
      const d = await call("GET", `/?${qs}`);
      setAssets(Array.isArray(d) ? d : (d?.assets || []));
    } catch { setAssets([]); } finally { setLoading(false); }
  }, [currentCompanyId, filters, call]);

  const loadAssetDetail = useCallback(async (id) => {
    try {
      const d = await call("GET", `/${id}`);
      setAssetDetail(d);
    } catch { }
  }, [call]);

  useEffect(() => { loadDashboard(); }, [loadDashboard]);
  useEffect(() => { if (tab === "register") loadAssets(); }, [tab, loadAssets]);
  useEffect(() => { if (selectedAsset && tab === "detail") loadAssetDetail(selectedAsset.id); }, [selectedAsset, tab, loadAssetDetail]);

  const deleteAsset = async (id) => {
    if (!window.confirm("Supprimer cette immobilisation ?")) return;
    try {
      await call("DELETE", `/${id}`);
      showToast("Immobilisation supprimée");
      loadAssets(); loadDashboard();
    } catch { showToast("Erreur suppression", "error"); }
  };

  const postDepreciation = async (id) => {
    try {
      await call("POST", `/${id}/post-depreciation`);
      showToast("Dotation comptabilisée");
      if (selectedAsset?.id === id) loadAssetDetail(id);
    } catch (e) { showToast("Erreur : " + e.message, "error"); }
  };

  const postAllDepreciation = async () => {
    if (!window.confirm("Comptabiliser toutes les dotations de l'exercice en cours ?")) return;
    try {
      await call("POST", "/post-all-depreciation", { company_id: currentCompanyId, year: new Date().getFullYear() });
      showToast("Toutes les dotations comptabilisées");
      loadDashboard();
    } catch (e) { showToast("Erreur : " + e.message, "error"); }
  };

  const dispose = async () => {
    if (!selectedAsset) return;
    try {
      await call("POST", `/${selectedAsset.id}/dispose`, { ...disposeForm, proceeds: parseFloat(disposeForm.proceeds) || 0 });
      showToast("Immobilisation cédée");
      setShowDisposeModal(false);
      loadAssets(); loadDashboard();
      if (assetDetail?.id === selectedAsset.id) loadAssetDetail(selectedAsset.id);
    } catch (e) { showToast("Erreur : " + e.message, "error"); }
  };

  const previewDepPlan = async () => {
    if (!form.acquisition_cost || !form.useful_life_years) return showToast("Remplissez le coût et la durée", "error");
    try {
      const d = await call("POST", "/preview-plan", { ...form, acquisition_cost: parseFloat(form.acquisition_cost), useful_life_years: parseInt(form.useful_life_years) });
      setPreviewPlan(Array.isArray(d) ? d : (d?.plan || []));
    } catch (e) { showToast("Erreur prévisualisation", "error"); }
  };

  const submitAsset = async () => {
    if (!form.name || !form.acquisition_cost || !form.useful_life_years || !form.acquisition_date) {
      return showToast("Remplissez les champs obligatoires *", "error");
    }
    setSubmitting(true);
    try {
      await call("POST", "/", { ...form, company_id: currentCompanyId, acquisition_cost: parseFloat(form.acquisition_cost), useful_life_years: parseInt(form.useful_life_years) });
      showToast("Immobilisation créée");
      setForm(EMPTY_FORM); setPreviewPlan(null);
      setTab("register"); loadDashboard();
    } catch (e) { showToast("Erreur création", "error"); } finally { setSubmitting(false); }
  };

  // Dashboard chart data
  const byCategory = dashboard?.by_category || assets.reduce((acc, a) => {
    const cat = a.category || "Autres";
    if (!acc[cat]) acc[cat] = { brut: 0, amort: 0 };
    acc[cat].brut += parseFloat(a.acquisition_cost || 0);
    acc[cat].amort += parseFloat(a.accumulated_depreciation || 0);
    return acc;
  }, {});
  const catChartData = Object.entries(byCategory).map(([name, v]) => ({ name: name.slice(0, 12), brut: v.brut || v, amort: v.amort || 0, vnc: (v.brut || v) - (v.amort || 0) }));
  const pieData = catChartData.filter(d => d.vnc > 0).map(d => ({ name: d.name, value: Math.round(d.vnc) }));

  const currentYear = new Date().getFullYear();

  const TABS = [
    { id: "dashboard", label: "📊 Tableau de bord" },
    { id: "register",  label: "📋 Registre" },
    { id: "detail",    label: "🔍 Fiche" },
    { id: "new",       label: "➕ Nouvelle" },
  ];

  return (
    <div style={{ padding: "0 0 40px" }}>
      {toast && (
        <div style={{ position: "fixed", top: 20, right: 20, zIndex: 9999, padding: "12px 20px", borderRadius: 10, background: toast.type === "error" ? "#ef4444" : "#1a9e72", color: "#fff", fontWeight: 700, fontSize: 13 }}>{toast.msg}</div>
      )}

      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 20, fontWeight: 800, color: "#111827" }}>🏗️ Immobilisations</h2>
          <div style={{ fontSize: 12, color: "#4b5563", marginTop: 2 }}>{currentSociete} — plan d'amortissement</div>
        </div>
        <button onClick={() => setTab("new")} style={{ ...S.btn, background: "#1a9e72", color: "#fff" }}>+ Nouvelle immobilisation</button>
      </div>

      <div style={{ display: "flex", gap: 4, marginBottom: 24, borderBottom: "1px solid #e5e7eb" }}>
        {TABS.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{ ...S.btn, background: "transparent", color: tab === t.id ? "#1a9e72" : "#4b5563", borderBottom: `2px solid ${tab === t.id ? "#1a9e72" : "transparent"}`, borderRadius: 0, padding: "8px 16px 10px" }}>{t.label}</button>
        ))}
      </div>

      {/* ── Dashboard ── */}
      {tab === "dashboard" && (
        <div>
          <div style={{ display: "flex", gap: 16, marginBottom: 24, flexWrap: "wrap" }}>
            {[
              { label: "Valeur brute totale", value: fmt(dashboard?.total_brut || 0) + " €", color: "#111827" },
              { label: "Amortissements cumulés", value: fmt(dashboard?.total_amort || 0) + " €", color: "#f59e0b" },
              { label: "Valeur nette comptable", value: fmt(dashboard?.total_vnc || 0) + " €", color: "#1a9e72" },
              { label: "Immobilisations actives", value: String(dashboard?.count_actifs || assets.filter(a => a.status === "actif" || !a.status).length || 0), color: "#185fa5" },
            ].map(k => (
              <div key={k.label} style={{ ...S.kpi, minWidth: 160 }}>
                <div style={{ fontSize: 11, color: "#4b5563", marginBottom: 6 }}>{k.label}</div>
                <div style={{ fontSize: 20, fontWeight: 800, color: k.color }}>{k.value}</div>
              </div>
            ))}
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, marginBottom: 20 }}>
            <div style={S.card}>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#111827", marginBottom: 16 }}>Par catégorie — Brut vs Amortissements</div>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={catChartData} margin={{ left: 0, right: 10 }}>
                  <XAxis dataKey="name" tick={{ fill: "#4b5563", fontSize: 10 }} />
                  <YAxis tick={{ fill: "#4b5563", fontSize: 11 }} />
                  <Tooltip contentStyle={{ background: "#ffffff", border: "1px solid #334155", borderRadius: 8 }} formatter={v => fmt(v) + " €"} />
                  <Legend />
                  <Bar dataKey="brut" name="Valeur brute" fill="#185fa5" radius={[3, 3, 0, 0]} />
                  <Bar dataKey="amort" name="Amortissements" fill="#f59e0b" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div style={S.card}>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#111827", marginBottom: 16 }}>Répartition VNC par catégorie</div>
              {pieData.length > 0 ? (
                <ResponsiveContainer width="100%" height={220}>
                  <PieChart>
                    <Pie data={pieData} cx="50%" cy="50%" innerRadius={50} outerRadius={90} dataKey="value" nameKey="name" label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`} labelLine={false} style={{ fontSize: 10 }}>
                      {pieData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                    </Pie>
                    <Tooltip contentStyle={{ background: "#ffffff", border: "1px solid #334155", borderRadius: 8 }} formatter={v => fmt(v) + " €"} />
                  </PieChart>
                </ResponsiveContainer>
              ) : <div style={{ textAlign: "center", padding: 40, color: "#4b5563" }}>Aucune donnée</div>}
            </div>
          </div>

          <div style={S.card}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#111827" }}>Dotations {currentYear} — actions</div>
              <button onClick={postAllDepreciation} style={{ ...S.btn, background: "#185fa5", color: "#fff", fontSize: 12 }}>✅ Comptabiliser tous les amortissements {currentYear}</button>
            </div>
            <div style={{ fontSize: 13, color: "#4b5563", padding: "10px 0" }}>Cliquez sur le bouton pour comptabiliser toutes les dotations aux amortissements de l'exercice {currentYear} en un seul clic. Chaque écriture sera créée dans la comptabilité (compte 681xxx / 28xxx).</div>
          </div>
        </div>
      )}

      {/* ── Register ── */}
      {tab === "register" && (
        <div>
          <div style={{ display: "flex", gap: 12, marginBottom: 16, flexWrap: "wrap" }}>
            <input placeholder="🔍 Rechercher…" value={filters.q} onChange={e => setFilters(f => ({ ...f, q: e.target.value }))}
              style={{ ...S.input, width: 220 }} />
            <select value={filters.category} onChange={e => setFilters(f => ({ ...f, category: e.target.value }))} style={{ ...S.input, width: 180 }}>
              <option value="">Toutes catégories</option>
              {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            <select value={filters.status} onChange={e => setFilters(f => ({ ...f, status: e.target.value }))} style={{ ...S.input, width: 140 }}>
              <option value="">Tous statuts</option>
              <option value="actif">Actif</option>
              <option value="cédé">Cédé</option>
              <option value="mis_au_rebut">Rebut</option>
            </select>
            <button onClick={loadAssets} style={{ ...S.btn, background: "#185fa5", color: "#fff" }}>🔄</button>
            <button onClick={() => downloadProtectedFile(api.immobilisations.exportRegisterDownload)} style={{ ...S.btn, background: "#f9fafb", color: "#4b5563" }}>⬇️ Exporter</button>
          </div>

          {loading ? <div style={{ textAlign: "center", padding: 40, color: "#4b5563" }}>Chargement…</div> : (
            <div style={S.card}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                <thead>
                  <tr style={{ borderBottom: "1px solid #e5e7eb" }}>
                    {["Réf", "Désignation", "Catégorie", "Acq.", "Coût HT", "Méthode", "Durée", "VNC", "Statut", "Actions"].map(h => (
                      <th key={h} style={{ padding: "8px 10px", textAlign: "left", color: "#4b5563", fontWeight: 600, fontSize: 10, textTransform: "uppercase" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {assets.map(a => (
                    <tr key={a.id} style={{ borderBottom: "1px solid #f3f4f6" }}>
                      <td style={{ padding: "8px 10px", color: "#4b5563", fontSize: 11 }}>{a.reference || "—"}</td>
                      <td style={{ padding: "8px 10px", color: "#111827", fontWeight: 600 }}>{a.name}</td>
                      <td style={{ padding: "8px 10px", color: "#4b5563", fontSize: 11 }}>{a.category}</td>
                      <td style={{ padding: "8px 10px", color: "#4b5563", fontSize: 11 }}>{a.acquisition_date?.slice(0, 10)}</td>
                      <td style={{ padding: "8px 10px", color: "#111827", fontWeight: 700 }}>{fmt(a.acquisition_cost)} €</td>
                      <td style={{ padding: "8px 10px", color: "#4b5563", fontSize: 11 }}>{a.depreciation_method === "linéaire" ? "Lin." : "Dég."}</td>
                      <td style={{ padding: "8px 10px", color: "#4b5563", fontSize: 11 }}>{a.useful_life_years} ans</td>
                      <td style={{ padding: "8px 10px", color: "#1a9e72", fontWeight: 700 }}>{fmt(a.current_book_value || (a.acquisition_cost - (a.accumulated_depreciation || 0)))} €</td>
                      <td style={{ padding: "8px 10px" }}><StatusBadge status={a.status || "actif"} /></td>
                      <td style={{ padding: "8px 10px" }}>
                        <div style={{ display: "flex", gap: 4 }}>
                          <button onClick={() => { setSelectedAsset(a); setTab("detail"); }} style={{ ...S.btn, padding: "3px 8px", background: "rgba(24,95,165,0.2)", color: "#60a5fa", fontSize: 11 }}>👁</button>
                          <button onClick={() => deleteAsset(a.id)} style={{ ...S.btn, padding: "3px 8px", background: "rgba(239,68,68,0.15)", color: "#ef4444", fontSize: 11 }}>🗑</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {assets.length === 0 && <tr><td colSpan={10} style={{ padding: 30, textAlign: "center", color: "#4b5563" }}>Aucune immobilisation trouvée</td></tr>}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── Detail ── */}
      {tab === "detail" && (
        <div>
          {!selectedAsset ? (
            <div style={{ textAlign: "center", padding: 80 }}>
              <div style={{ fontSize: 48, marginBottom: 16 }}>🔍</div>
              <div style={{ color: "#111827", fontWeight: 700, marginBottom: 8 }}>Sélectionnez une immobilisation</div>
              <div style={{ color: "#4b5563", fontSize: 13 }}>Cliquez sur l'icône 👁 dans le registre pour voir la fiche.</div>
              <button onClick={() => setTab("register")} style={{ marginTop: 20, ...S.btn, background: "#185fa5", color: "#fff" }}>Voir le registre</button>
            </div>
          ) : (
            <div>
              <div style={{ ...S.card, marginBottom: 20 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20 }}>
                  <div>
                    <h3 style={{ margin: 0, color: "#111827", fontSize: 18 }}>{assetDetail?.name || selectedAsset.name}</h3>
                    <div style={{ display: "flex", gap: 10, marginTop: 8 }}>
                      <span style={{ fontSize: 12, padding: "2px 10px", borderRadius: 20, background: "rgba(24,95,165,0.2)", color: "#60a5fa" }}>{assetDetail?.category || selectedAsset.category}</span>
                      <StatusBadge status={assetDetail?.status || selectedAsset.status || "actif"} />
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 10 }}>
                    <button onClick={() => postDepreciation(selectedAsset.id)} style={{ ...S.btn, background: "#185fa5", color: "#fff", fontSize: 12 }}>✅ Comptabiliser {currentYear}</button>
                    <button onClick={() => setShowDisposeModal(true)} style={{ ...S.btn, background: "rgba(245,158,11,0.2)", color: "#f59e0b", fontSize: 12 }}>📤 Céder</button>
                  </div>
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
                  <div>
                    <div style={{ color: "#4b5563", fontSize: 11, marginBottom: 12, textTransform: "uppercase", letterSpacing: "0.05em" }}>Acquisition</div>
                    {[
                      { label: "Référence", value: assetDetail?.reference || selectedAsset.reference || "—" },
                      { label: "Date d'acquisition", value: (assetDetail?.acquisition_date || selectedAsset.acquisition_date || "").slice(0, 10) },
                      { label: "Coût d'acquisition HT", value: fmt(assetDetail?.acquisition_cost || selectedAsset.acquisition_cost) + " €" },
                      { label: "Fournisseur", value: assetDetail?.supplier || selectedAsset.supplier || "—" },
                      { label: "Localisation", value: assetDetail?.location || selectedAsset.location || "—" },
                    ].map(r => (
                      <div key={r.label} style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", borderBottom: "1px solid #f3f4f6" }}>
                        <span style={{ color: "#4b5563", fontSize: 12 }}>{r.label}</span>
                        <span style={{ color: "#111827", fontSize: 12, fontWeight: 600 }}>{r.value}</span>
                      </div>
                    ))}
                  </div>
                  <div>
                    <div style={{ color: "#4b5563", fontSize: 11, marginBottom: 12, textTransform: "uppercase", letterSpacing: "0.05em" }}>Amortissement</div>
                    {[
                      { label: "Méthode", value: assetDetail?.depreciation_method || selectedAsset.depreciation_method },
                      { label: "Durée de vie", value: (assetDetail?.useful_life_years || selectedAsset.useful_life_years) + " ans" },
                      { label: "Amortissements cumulés", value: fmt(assetDetail?.accumulated_depreciation || selectedAsset.accumulated_depreciation || 0) + " €" },
                      { label: "Valeur nette comptable", value: fmt(assetDetail?.current_book_value || (selectedAsset.acquisition_cost - (selectedAsset.accumulated_depreciation || 0))) + " €" },
                    ].map(r => (
                      <div key={r.label} style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", borderBottom: "1px solid #f3f4f6" }}>
                        <span style={{ color: "#4b5563", fontSize: 12 }}>{r.label}</span>
                        <span style={{ color: "#111827", fontSize: 12, fontWeight: 600 }}>{r.value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {assetDetail?.depreciation_plan?.length > 0 && (
                <div style={{ ...S.card, marginBottom: 20 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: "#111827", marginBottom: 16 }}>Plan d'amortissement</div>
                  <div style={{ overflowX: "auto" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                      <thead><tr style={{ borderBottom: "1px solid #e5e7eb" }}>
                        {["Exercice", "Dotation annuelle", "Amort. cumulés", "VNC fin d'exercice", "Comptabilisé"].map(h => (
                          <th key={h} style={{ padding: "8px 12px", textAlign: "left", color: "#4b5563", fontWeight: 600, fontSize: 11, textTransform: "uppercase" }}>{h}</th>
                        ))}
                      </tr></thead>
                      <tbody>
                        {assetDetail.depreciation_plan.map((row, i) => (
                          <tr key={i} style={{ borderBottom: "1px solid #f3f4f6", background: row.year === currentYear ? "rgba(24,95,165,0.1)" : "transparent" }}>
                            <td style={{ padding: "8px 12px", color: row.year === currentYear ? "#60a5fa" : "#4b5563", fontWeight: row.year === currentYear ? 700 : 400 }}>{row.year}</td>
                            <td style={{ padding: "8px 12px", color: "#f59e0b", fontWeight: 700 }}>{fmt(row.dotation)} €</td>
                            <td style={{ padding: "8px 12px", color: "#111827" }}>{fmt(row.cumul)} €</td>
                            <td style={{ padding: "8px 12px", color: "#1a9e72", fontWeight: 700 }}>{fmt(row.vnc)} €</td>
                            <td style={{ padding: "8px 12px" }}>{row.posted ? "✅" : "—"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div style={{ marginTop: 20 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: "#111827", marginBottom: 12 }}>Déclin de la valeur nette</div>
                    <ResponsiveContainer width="100%" height={180}>
                      <LineChart data={assetDetail.depreciation_plan} margin={{ left: 0, right: 10 }}>
                        <XAxis dataKey="year" tick={{ fill: "#4b5563", fontSize: 11 }} />
                        <YAxis tick={{ fill: "#4b5563", fontSize: 11 }} />
                        <Tooltip contentStyle={{ background: "#ffffff", border: "1px solid #334155", borderRadius: 8 }} formatter={v => fmt(v) + " €"} />
                        <Line type="monotone" dataKey="vnc" name="VNC" stroke="#1a9e72" strokeWidth={2} dot={{ fill: "#1a9e72" }} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ── New ── */}
      {tab === "new" && (
        <div style={S.card}>
          <h3 style={{ margin: "0 0 20px", color: "#111827" }}>Nouvelle immobilisation</h3>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 20 }}>
            <div>
              <label style={S.label}>Désignation *</label>
              <input value={form.name} onChange={e => setForm(o => ({ ...o, name: e.target.value }))} style={S.input} />
            </div>
            <div>
              <label style={S.label}>Référence</label>
              <input value={form.reference} onChange={e => setForm(o => ({ ...o, reference: e.target.value }))} style={S.input} />
            </div>
            <div>
              <label style={S.label}>Catégorie *</label>
              <select value={form.category} onChange={e => setForm(o => ({ ...o, category: e.target.value }))} style={S.input}>
                {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label style={S.label}>Date d'acquisition *</label>
              <input type="date" value={form.acquisition_date} onChange={e => setForm(o => ({ ...o, acquisition_date: e.target.value }))} style={S.input} />
            </div>
            <div>
              <label style={S.label}>Coût d'acquisition HT (€) *</label>
              <input type="number" min="0" step="0.01" value={form.acquisition_cost} onChange={e => setForm(o => ({ ...o, acquisition_cost: e.target.value }))} style={S.input} />
            </div>
            <div>
              <label style={S.label}>Méthode d'amortissement *</label>
              <select value={form.depreciation_method} onChange={e => setForm(o => ({ ...o, depreciation_method: e.target.value }))} style={S.input}>
                <option value="linéaire">Linéaire</option>
                <option value="dégressif">Dégressif</option>
              </select>
            </div>
            <div>
              <label style={S.label}>Durée de vie (années) *</label>
              <input type="number" min="1" max="50" value={form.useful_life_years} onChange={e => setForm(o => ({ ...o, useful_life_years: e.target.value }))} style={S.input} />
            </div>
            <div>
              <label style={S.label}>Localisation</label>
              <input value={form.location} onChange={e => setForm(o => ({ ...o, location: e.target.value }))} style={S.input} />
            </div>
            <div>
              <label style={S.label}>Fournisseur</label>
              <input value={form.supplier} onChange={e => setForm(o => ({ ...o, supplier: e.target.value }))} style={S.input} />
            </div>
            <div style={{ gridColumn: "1/-1" }}>
              <label style={S.label}>Notes</label>
              <textarea value={form.notes} onChange={e => setForm(o => ({ ...o, notes: e.target.value }))} rows={3} style={{ ...S.input, resize: "vertical" }} />
            </div>
          </div>

          <div style={{ display: "flex", gap: 12, marginBottom: previewPlan ? 20 : 0 }}>
            <button onClick={previewDepPlan} style={{ ...S.btn, background: "rgba(24,95,165,0.2)", color: "#60a5fa" }}>🔮 Prévisualiser le plan</button>
            <button onClick={submitAsset} disabled={submitting} style={{ ...S.btn, background: "#1a9e72", color: "#fff", opacity: submitting ? 0.6 : 1 }}>{submitting ? "Création…" : "✅ Enregistrer"}</button>
            <button onClick={() => { setForm(EMPTY_FORM); setPreviewPlan(null); }} style={{ ...S.btn, background: "#f9fafb", color: "#4b5563" }}>Réinitialiser</button>
          </div>

          {previewPlan && previewPlan.length > 0 && (
            <div style={{ ...S.card, background: "#f9fafb", marginTop: 20 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#111827", marginBottom: 12 }}>Plan d'amortissement prévisionnel</div>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                <thead><tr style={{ borderBottom: "1px solid #e5e7eb" }}>
                  {["Exercice", "Dotation", "Amort. cumulés", "VNC"].map(h => (
                    <th key={h} style={{ padding: "6px 10px", textAlign: "left", color: "#4b5563", fontWeight: 600 }}>{h}</th>
                  ))}
                </tr></thead>
                <tbody>
                  {previewPlan.map((row, i) => (
                    <tr key={i} style={{ borderBottom: "1px solid #f3f4f6" }}>
                      <td style={{ padding: "6px 10px", color: "#4b5563" }}>{row.year}</td>
                      <td style={{ padding: "6px 10px", color: "#f59e0b", fontWeight: 700 }}>{fmt(row.dotation)} €</td>
                      <td style={{ padding: "6px 10px", color: "#111827" }}>{fmt(row.cumul)} €</td>
                      <td style={{ padding: "6px 10px", color: "#1a9e72", fontWeight: 700 }}>{fmt(row.vnc)} €</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── Dispose Modal ── */}
      {showDisposeModal && (
        <div onClick={e => e.target === e.currentTarget && setShowDisposeModal(false)}
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div style={{ background: "#ffffff", borderRadius: 14, padding: 28, width: 420 }}>
            <h3 style={{ margin: "0 0 20px", color: "#111827" }}>📤 Cession de l'immobilisation</h3>
            {[
              { label: "Date de cession", key: "disposal_date", type: "date" },
              { label: "Prix de cession (€)", key: "proceeds", type: "number" },
            ].map(f => (
              <div key={f.key} style={{ marginBottom: 14 }}>
                <label style={S.label}>{f.label}</label>
                <input type={f.type} value={disposeForm[f.key]} onChange={e => setDisposeForm(o => ({ ...o, [f.key]: e.target.value }))} style={S.input} />
              </div>
            ))}
            <div style={{ marginBottom: 20 }}>
              <label style={S.label}>Motif</label>
              <textarea value={disposeForm.notes} onChange={e => setDisposeForm(o => ({ ...o, notes: e.target.value }))} rows={3} style={{ ...S.input, resize: "none" }} />
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              <button onClick={dispose} style={{ ...S.btn, background: "#f59e0b", color: "#fff" }}>Confirmer la cession</button>
              <button onClick={() => setShowDisposeModal(false)} style={{ ...S.btn, background: "#f9fafb", color: "#4b5563" }}>Annuler</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
